// DataDictionaryFindingsPanel — Phase 4 backbone UI (2026-05-29).
//
// Surfaces PEG-DATA-DICT-COMPLETENESS-001 resolver findings on the
// G3.7 review surface. The data-dictionary_author agent's output
// is read-only here — the architect fixes findings either by
// editing the data-dictionary directly OR by adding a waiver to
// phase4/audit-diff.waivers.json (the existing PEG-waiver
// mechanism). A future sprint may add a per-finding override
// modal here, parallel to the Deployment Decisions panel.
//
// Layout:
//
//   ┌─────────────────────────────────────────────────────────────┐
//   │ Header: profile chips · "N findings across M tables"        │
//   ├─────────────────────────────────────────────────────────────┤
//   │ Per-table accordion. Tables with findings expanded by       │
//   │ default; tables without findings collapsed.                 │
//   │   ⌄ audit_log · 2 findings                                  │
//   │     • [critical] missing_mandatory_column · trace_id        │
//   │       Suggested: Add a `trace_id` column to the audit_log…  │
//   │     • [high] retention_below_floor                          │
//   │       Suggested: Either align the retention text…           │
//   │   ⌃ (no findings) financial_ledger                          │
//   ├─────────────────────────────────────────────────────────────┤
//   │ Help footer: how to remediate / waive                       │
//   └─────────────────────────────────────────────────────────────┘

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Info,
  Loader2,
  Pencil,
  ShieldOff,
} from 'lucide-react'

import { getDataDictionaryFindings } from '@/lib/dataDictionaryFindingsApi'
import { FINDING_PRESENTATION } from '@/lib/findingPresentation'
import {
  DD_INVARIANT_ID,
  DD_KIND_LABEL,
  DD_RETENTION_FIX_KINDS,
  DD_SEVERITY_PALETTE,
  type DataDictionaryFindingRow,
  type DataDictionaryFindingsResponse,
} from '@/types/dataDictionaryFindings'
import { ApplyRetentionFixModal } from './ApplyRetentionFixModal'
import { WaivedFindingsDisclosure } from './WaivedFindingsDisclosure'
import { WaiveFindingModal } from './WaiveFindingModal'

export interface DataDictionaryFindingsPanelProps {
  projectId: string
  runId: number
}

interface GroupedFindings {
  /** Table name OR null for findings with no table (e.g., when
   *  ``audit_log`` mandate is unmet and no entry exists). */
  tableName: string | null
  findings: DataDictionaryFindingRow[]
}

function groupByTable(
  rows: DataDictionaryFindingRow[],
  inventory: string[],
): GroupedFindings[] {
  // First: groups for every table in inventory (so tables without
  // findings still show as "clean" rows).
  const byTable = new Map<string | null, DataDictionaryFindingRow[]>()
  for (const t of inventory) byTable.set(t, [])
  // Second: bucket findings; findings whose table_name doesn't
  // match an inventory entry (rare) land under their own bucket.
  // null-table findings (e.g. missing-mandatory-table where the
  // table doesn't exist yet) land in the leading null group.
  for (const f of rows) {
    const key = f.table_name
    if (!byTable.has(key)) byTable.set(key, [])
    byTable.get(key)!.push(f)
  }
  // Order: missing-table findings (null key) first; then tables
  // with findings (count desc); then clean tables alphabetically.
  const groups: GroupedFindings[] = []
  if (byTable.has(null)) {
    groups.push({ tableName: null, findings: byTable.get(null)! })
    byTable.delete(null)
  }
  const entries = Array.from(byTable.entries())
  entries.sort((a, b) => {
    const ca = a[1].length
    const cb = b[1].length
    if (ca !== cb) return cb - ca
    return (a[0] || '').localeCompare(b[0] || '')
  })
  for (const [name, findings] of entries) {
    groups.push({ tableName: name, findings })
  }
  return groups
}

function FindingCard({
  row,
  onWaive,
  onApplyFix,
}: {
  row: DataDictionaryFindingRow
  onWaive: () => void
  onApplyFix: () => void
}) {
  const sev = DD_SEVERITY_PALETTE[row.severity]
  const canApplyFix =
    row.table_name !== null &&
    DD_RETENTION_FIX_KINDS.includes(row.finding_kind)
  return (
    <div className="rounded border border-slate-700/50 bg-slate-950/40 p-2.5 text-sm">
      <div className="mb-1 flex flex-wrap items-center gap-1.5">
        <span
          className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10.5px] font-medium ${sev.chipClass}`}
        >
          {sev.label}
        </span>
        <span className="text-[10.5px] uppercase tracking-wide text-slate-500">
          {DD_KIND_LABEL[row.finding_kind]}
        </span>
        {row.column_name && (
          <span className="font-mono text-xs text-slate-300">
            .{row.column_name}
          </span>
        )}
      </div>
      <div className="text-slate-200">
        {FINDING_PRESENTATION[row.finding_kind].whyItMatters}
      </div>
      {/* Blast radius (Stage 3, D6): the table's reach — how many
          requirements reference it. "backs" (not "affects") is honest:
          referenced_by is the SRs referencing the table, a reach proxy. */}
      {typeof row.backs_requirement_count === 'number' &&
        row.backs_requirement_count > 0 && (
          <div
            data-testid="dd-finding-blast-radius"
            className="mt-1 text-[11px] text-slate-300"
          >
            <span className="font-mono text-slate-200">{row.table_name}</span>{' '}
            backs {row.backs_requirement_count} requirement
            {row.backs_requirement_count === 1 ? '' : 's'}
          </div>
        )}
      {row.suggested_fix && (
        <div className="mt-1.5 rounded border border-emerald-900/30 bg-emerald-950/15 p-1.5 text-xs text-emerald-200/85">
          <strong className="text-emerald-300">Suggested: </strong>
          {row.suggested_fix}
        </div>
      )}
      {/* Pipeline detail demoted to a collapsible disclosure (legibility
          pass) — the raw resolver message, citations, and rule source. */}
      <details className="mt-1.5 text-[10.5px] text-slate-500">
        <summary className="cursor-pointer select-none text-slate-400">
          Developer detail
        </summary>
        <div className="mt-1 text-slate-400">{row.message}</div>
        {row.cites.length > 0 && (
          <div className="mt-1 text-slate-400">
            cite: {row.cites.join(' · ')}
          </div>
        )}
        <div className="mt-1">
          rule source: <span className="font-mono">{row.rule_source}</span>
        </div>
      </details>
      {/* Resolution actions — the in-UI replacement for hand-editing
          data-dictionary.json / authoring a waiver by hand. */}
      <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-800/50 pt-2">
        {canApplyFix && (
          <button
            type="button"
            onClick={onApplyFix}
            className="inline-flex items-center gap-1 rounded border border-sky-700/50 bg-sky-950/30 px-2 py-1 text-[11px] font-medium text-sky-200 hover:bg-sky-900/40"
          >
            <Pencil className="h-3 w-3" /> Apply fix
          </button>
        )}
        <button
          type="button"
          onClick={onWaive}
          className="inline-flex items-center gap-1 rounded border border-amber-700/40 bg-amber-950/20 px-2 py-1 text-[11px] font-medium text-amber-200 hover:bg-amber-900/30"
        >
          <ShieldOff className="h-3 w-3" /> Waive
        </button>
      </div>
    </div>
  )
}

function TableGroup({
  group,
  defaultExpanded,
  onWaive,
  onApplyFix,
}: {
  group: GroupedFindings
  defaultExpanded: boolean
  onWaive: (row: DataDictionaryFindingRow) => void
  onApplyFix: (row: DataDictionaryFindingRow) => void
}) {
  const [open, setOpen] = useState(defaultExpanded)
  const findingsCount = group.findings.length
  const isClean = findingsCount === 0
  const isMissing = group.tableName === null
  return (
    <div
      className={`rounded-md border bg-slate-900/40 overflow-hidden ${
        isMissing
          ? 'border-red-800/50'
          : isClean
            ? 'border-slate-800/50'
            : 'border-amber-800/40'
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-slate-900/60"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        <span className="font-mono text-sm font-semibold text-slate-100">
          {group.tableName ?? '(missing tables)'}
        </span>
        {isClean ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-300">
            <CheckCircle2 className="h-3 w-3" /> clean
          </span>
        ) : (
          <span
            className={`inline-flex items-center gap-1 text-xs ${
              isMissing ? 'text-red-300' : 'text-amber-300'
            }`}
          >
            <AlertTriangle className="h-3 w-3" />
            {findingsCount} {findingsCount === 1 ? 'finding' : 'findings'}
          </span>
        )}
      </button>
      {open && !isClean && (
        <div className="flex flex-col gap-2 px-3 pb-3 pt-1">
          {group.findings.map((f, idx) => (
            <FindingCard
              key={`${f.finding_kind}-${idx}`}
              row={f}
              onWaive={() => onWaive(f)}
              onApplyFix={() => onApplyFix(f)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function DataDictionaryFindingsPanel({
  projectId,
  runId,
}: DataDictionaryFindingsPanelProps) {
  const queryKey = ['data-dictionary-findings', projectId, runId]
  const { data, isLoading, error } = useQuery<DataDictionaryFindingsResponse>({
    queryKey,
    queryFn: () => getDataDictionaryFindings(projectId, runId),
  })

  const [waiving, setWaiving] = useState<DataDictionaryFindingRow | null>(null)
  const [fixing, setFixing] = useState<DataDictionaryFindingRow | null>(null)

  // Waived findings are filtered out of the active groups (so they
  // don't inflate the counts) and surfaced in the disclosure below.
  const activeFindings = useMemo(
    () => (data?.findings ?? []).filter((f) => !f.waived),
    [data],
  )
  const waivedFindings = useMemo(
    () => (data?.findings ?? []).filter((f) => f.waived),
    [data],
  )

  const groups = useMemo(() => {
    if (!data) return [] as GroupedFindings[]
    return groupByTable(activeFindings, data.table_inventory)
  }, [data, activeFindings])

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading data-dictionary findings…
      </div>
    )
  }
  if (error) {
    return (
      <div className="rounded border border-red-800 bg-red-950/30 p-3 text-sm text-red-200">
        Failed to load data-dictionary findings:{' '}
        {String((error as Error).message)}
      </div>
    )
  }
  if (!data) return null

  if (!data.data_dictionary_present) {
    return (
      <div className="rounded border border-slate-700 bg-slate-900/50 p-3 text-sm text-slate-300">
        <div className="mb-1 flex items-center gap-2 text-slate-200">
          <Info className="h-4 w-4 text-sky-400" />
          Phase 3 hasn't shipped data-dictionary.json yet
        </div>
        <p className="text-xs text-slate-400">
          The data_dictionary_author agent runs in Phase 3's wave 3.
          Once it produces{' '}
          <code className="rounded bg-slate-800 px-1 py-0.5">
            phase3/data-dictionary.json
          </code>
          , this panel will show any regulatory-mandate or retention
          findings the resolver detects.
        </p>
        {data.findings.length > 0 && (
          <div className="mt-2 text-xs text-amber-300">
            Heads-up: {data.findings.length} mandate-miss finding(s)
            already pending. Phase 3 needs to produce these tables /
            columns before export will clear PEG-DATA-DICT-COMPLETENESS-001.
          </div>
        )}
      </div>
    )
  }

  const totalFindings = activeFindings.length
  const tablesWithFindings = groups.filter(
    (g) => g.findings.length > 0 && g.tableName !== null,
  ).length

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex flex-wrap items-baseline gap-3 text-xs text-slate-400">
        {data.regulatory.length > 0 && (
          <span>regulatory: {data.regulatory.join(', ')}</span>
        )}
        {data.shape && <span>shape: {data.shape}</span>}
        <span>{data.table_inventory.length} tables in data dictionary</span>
        {totalFindings > 0 ? (
          <span className="text-amber-300">
            {totalFindings} {totalFindings === 1 ? 'finding' : 'findings'}
            {tablesWithFindings > 0 && (
              <> across {tablesWithFindings} table(s)</>
            )}
          </span>
        ) : (
          <span className="text-emerald-300">
            <CheckCircle2 className="mr-1 inline-block h-3 w-3" />
            clean
          </span>
        )}
      </div>

      {/* Groups */}
      {groups.length === 0 ? (
        <div className="rounded border border-slate-700 bg-slate-900/40 p-3 text-sm text-slate-400">
          No tables in the data dictionary yet.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {groups.map((g) => (
            <TableGroup
              key={g.tableName ?? '__missing__'}
              group={g}
              defaultExpanded={g.findings.length > 0}
              onWaive={setWaiving}
              onApplyFix={setFixing}
            />
          ))}
        </div>
      )}

      {/* Waived findings — accepted, collapsed out of the way. */}
      <WaivedFindingsDisclosure
        items={waivedFindings.map((f, idx) => ({
          id: `${f.finding_kind}-${f.table_name ?? ''}-${idx}`,
          label: `${DD_KIND_LABEL[f.finding_kind]}${
            f.table_name ? ` · ${f.table_name}` : ''
          }${f.column_name ? `.${f.column_name}` : ''}`,
          justification: f.waiver_justification,
        }))}
      />

      {/* Resolution modals (portal siblings). */}
      {waiving && (
        <WaiveFindingModal
          projectId={projectId}
          runId={runId}
          invariantId={DD_INVARIANT_ID}
          findingKey={waiving.finding_key}
          findingLabel={`${DD_KIND_LABEL[waiving.finding_kind]}${
            waiving.table_name ? ` · ${waiving.table_name}` : ''
          }`}
          refetchQueryKey={queryKey}
          onClose={() => setWaiving(null)}
        />
      )}
      {fixing && fixing.table_name && (
        <ApplyRetentionFixModal
          projectId={projectId}
          runId={runId}
          tableName={fixing.table_name}
          suggestedFix={fixing.suggested_fix}
          initialValue={fixing.suggested_retention ?? undefined}
          suggestionSource={
            fixing.suggested_retention
              ? fixing.cites.length > 0
                ? 'profile'
                : 'provisional'
              : null
          }
          refetchQueryKey={queryKey}
          onClose={() => setFixing(null)}
        />
      )}

      {/* Help footer */}
      <div className="rounded border border-slate-800 bg-slate-900/40 p-3 text-xs text-slate-400">
        <div className="mb-1 flex items-center gap-2 text-slate-300">
          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
          How to clear these findings
        </div>
        <p>
          <strong className="text-sky-200">Retention findings</strong> (TBD
          / below-floor): click <strong>Apply fix</strong> to set the
          table's retention to a concrete duration citing the governing
          standard (e.g. <code>"12 months per PCI DSS Req 10.5.1"</code>) —
          no JSON editing.{' '}
          <strong className="text-amber-200">False positive or
          out-of-scope:</strong>{' '}
          click <strong>Waive</strong> and record a justification; it lands
          in the audit trail and the pre-export gate stops blocking on it.{' '}
          <strong className="text-slate-300">Missing tables / columns</strong>{' '}
          need real content — re-emit Phase 3 with the entity in the
          agent's seed (or Waive if genuinely out of scope).
        </p>
      </div>
    </div>
  )
}
